/**
 * O cliente pedindo horário.
 *
 * Ele escolhe **o que** vai fazer — um serviço ou vários — e nunca quanto
 * tempo leva: o tempo dele já está na ficha da barbearia, então a lista de
 * horários que aparece aqui já é a lista que cabe para ele. Um cliente de 25
 * minutos vê opções que um de 55 não vê, e nenhum dos dois precisa saber disso.
 *
 * Quando a combinação escolhida não cabe no dia, a tela diz o tamanho que não
 * coube e o que ainda caberia. "Nenhum horário disponível" faz a pessoa fechar
 * o app achando que a barbearia está lotada, quando na verdade dava para fazer
 * o cabelo.
 */

import type {
  AvailableSlotView,
  DayAvailabilityView,
  FittableSubsetView,
  ServiceView,
} from '@barbearia/shared';
import { describeServices, describeUnavailable } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../../App.tsx';
import { Button, EmptyState, ErrorNote, OptionList, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { addDays, duration, money, relativeDay, timeOf, todayIn } from '../../lib/format.ts';
import { JoinWaitlistSheet } from './MyWaitlist.tsx';

interface StaffMember {
  id: string;
  name: string;
}

export function Book() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const today = todayIn(tenant.timezone);
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [date, setDate] = useState(today);
  const [slot, setSlot] = useState<AvailableSlotView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [waiting, setWaiting] = useState(false);

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.get<{ staff: StaffMember[] }>('/schedule/staff'),
  });

  // O catálogo vem daqui, e não de `tenant.services`: aquele obedece ao ajuste
  // de vitrine da barbearia, que pode esconder preço — ou tudo — de quem não
  // entrou. Quem está nesta tela já entrou e precisa ver o que dá para marcar.
  const { data: catalog } = useQuery({
    queryKey: ['services-available'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services/available'),
  });

  const services = catalog?.services ?? [];
  const chosen = serviceIds
    .map((id) => services.find((service) => service.id === id))
    .filter((service): service is ServiceView => service !== undefined);

  /** Escolher e desescolher no mesmo toque, sem perder o horário à toa. */
  const toggleService = (id: string) => {
    setServiceIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
    setSlot(null);
  };

  const { data: availability, isFetching } = useQuery({
    queryKey: ['availability', date, serviceIds.join(','), staffId],
    queryFn: () =>
      api.get<{ days: DayAvailabilityView[] }>('/schedule/availability', {
        date,
        serviceIds: serviceIds.join(','),
        membershipId: staffId ?? undefined,
        days: 1,
      }),
    enabled: serviceIds.length > 0,
  });

  const request = useMutation({
    mutationFn: () =>
      api.post('/appointments/request', {
        serviceIds,
        membershipId: staffId ?? undefined,
        startsAt: slot?.startsAt,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-appointments'] });
      navigate('/', { replace: true });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const days = availability?.days ?? [];
  // Sem profissional escolhido a API responde por todos; juntamos os horários
  // e guardamos de quem é cada um.
  const offers = days.flatMap((day) =>
    day.slots.map((entry) => ({
      ...entry,
      membershipId: day.membershipId,
      name: day.membershipName,
    })),
  );
  const reason = days.find((day) => day.reason)?.reason;
  const totalMinutes = days[0]?.durationMinutes ?? 0;

  // Com vários profissionais, a melhor notícia manda: se na agenda do Rafa cabe
  // cabelo e barba e na do Zé só o cabelo, é a primeira que interessa.
  const fits = bestFit(days);
  const fitNames = fits
    ? fits.serviceIds.map((id) => services.find((service) => service.id === id)?.name ?? '')
    : [];

  // A barra de baixo só existe quando há o que escolher. Num dia sem vaga
  // nenhuma ela seria um "Escolha um horário" desabilitado ocupando a faixa
  // mais valiosa da tela, logo abaixo do botão que realmente resolve.
  const showFooter = offers.length > 0 || slot !== null;

  return (
    <div className={`flex min-h-full flex-col ${showFooter ? 'pb-32' : 'pb-8'}`}>
      <ScreenHeader title="Agendar" />

      <div className="flex flex-col gap-6 px-4 pt-4">
        <section className="flex flex-col gap-2">
          <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            O que você vai fazer
          </h2>
          <OptionList
            items={services}
            selected={serviceIds}
            getKey={(service) => service.id}
            onSelect={(service) => toggleService(service.id)}
            renderTitle={(service) => service.name}
            renderMeta={(service) => money(service.priceCents)}
          />
          {chosen.length > 1 && (
            <p className="text-[13px] text-ink-soft">
              {describeServices(chosen.map((service) => service.name))} ·{' '}
              {money(chosen.reduce((sum, service) => sum + service.priceCents, 0))}
            </p>
          )}
        </section>

        {serviceIds.length > 0 && (staff?.staff.length ?? 0) > 1 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
              Com quem
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setStaffId(null);
                  setSlot(null);
                }}
                className={`rounded-full px-4 py-2 text-[14px] font-medium ${
                  staffId === null
                    ? 'bg-accent text-accent-ink'
                    : 'border border-line bg-surface-raised text-ink-soft'
                }`}
              >
                Tanto faz
              </button>
              {staff?.staff.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => {
                    setStaffId(member.id);
                    setSlot(null);
                  }}
                  className={`rounded-full px-4 py-2 text-[14px] font-medium ${
                    staffId === member.id
                      ? 'bg-accent text-accent-ink'
                      : 'border border-line bg-surface-raised text-ink-soft'
                  }`}
                >
                  {member.name}
                </button>
              ))}
            </div>
          </section>
        )}

        {serviceIds.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
              Quando
            </h2>

            <div className="scroll-touch -mx-4 flex gap-1.5 overflow-x-auto px-4">
              {Array.from({ length: 21 }, (_, index) => addDays(today, index)).map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    setDate(day);
                    setSlot(null);
                  }}
                  className={`shrink-0 rounded-lg px-3 py-2 text-[13px] font-medium ${
                    day === date ? 'bg-primary text-primary-ink' : 'bg-surface-raised text-ink-soft'
                  }`}
                >
                  {relativeDay(day, today, tenant.timezone)}
                </button>
              ))}
            </div>

            {isFetching && <Spinner />}

            {/*
              O dia sem horário é o momento de maior intenção que existe nesta
              tela: a pessoa já escolheu o que quer e acabou de descobrir que
              não tem vaga. É aqui que "o que ainda cabe" e "me avisa quando
              vagar" precisam estar — e não escondidos em outra tela, depois que
              ela desistiu.
            */}
            {!isFetching && offers.length === 0 && (
              <EmptyState
                title={describeUnavailable({ reason, durationMinutes: totalMinutes })}
                description={
                  fits
                    ? `Dá para fazer ${describeServices(fitNames)} — ${duration(fits.durationMinutes)} — neste dia. Ou escolha outro dia na faixa acima.`
                    : 'Escolha outro dia na faixa acima, ou peça para ser avisado quando abrir uma vaga.'
                }
                action={
                  <div className="flex flex-col gap-2">
                    {fits && (
                      <Button
                        onClick={() => {
                          setServiceIds(fits.serviceIds);
                          setSlot(null);
                        }}
                      >
                        Agendar só {describeServices(fitNames).toLowerCase()}
                      </Button>
                    )}
                    <Button
                      variant={fits ? 'secondary' : 'primary'}
                      onClick={() => setWaiting(true)}
                    >
                      Avisar quando vagar
                    </Button>
                  </div>
                }
              />
            )}

            {!isFetching && offers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {offers.map((offer) => (
                  <button
                    key={`${offer.membershipId}-${offer.startsAt}`}
                    type="button"
                    onClick={() => {
                      setSlot(offer);
                      setStaffId(offer.membershipId);
                    }}
                    className={`touch-target rounded-lg px-4 py-2.5 text-[15px] font-semibold tabular-nums ${
                      slot?.startsAt === offer.startsAt
                        ? 'bg-primary text-primary-ink'
                        : 'border border-line bg-surface-raised text-ink'
                    }`}
                  >
                    {timeOf(offer.startsAt, tenant.timezone)}
                  </button>
                ))}
              </div>
            )}

            {days[0] && offers.length > 0 && (
              <p className="text-[12px] text-ink-faint">
                Seu atendimento leva {duration(totalMinutes)} — os horários acima já consideram
                isso.
              </p>
            )}

            {/* Tem horário, mas nenhum serve. Discreto de propósito: quem achou
                o que queria não precisa desviar o olho para ler isto. */}
            {!isFetching && offers.length > 0 && (
              <button
                type="button"
                onClick={() => setWaiting(true)}
                className="touch-target self-start text-[14px] text-accent"
              >
                Nenhum horário bom? Avisar quando vagar
              </button>
            )}
          </section>
        )}

        {error && <ErrorNote>{error.message}</ErrorNote>}
      </div>

      {/*
        Fundo sólido, e não transparente: este botão fica desabilitado enquanto
        ninguém escolheu horário, e um botão desabilitado é translúcido — sem o
        fundo, a lista de horários passa por baixo dele e o texto vira sopa.
      */}
      {showFooter && (
        <div className="pb-safe sticky bottom-0 z-40 border-t border-line bg-surface px-4 pt-3">
          <Button
            full
            className="shadow-lg"
            disabled={!slot}
            loading={request.isPending}
            onClick={() => request.mutate()}
          >
            {slot ? `Pedir ${timeOf(slot.startsAt, tenant.timezone)}` : 'Escolha um horário'}
          </Button>
        </div>
      )}

      {waiting && (
        <JoinWaitlistSheet
          defaultServiceIds={serviceIds}
          membershipId={staffId}
          onClose={() => setWaiting(false)}
        />
      )}
    </div>
  );
}

/**
 * O melhor recorte oferecido entre as agendas do dia.
 *
 * Mais serviços ganha de menos; empatado, o mais longo. É a mesma ordem que o
 * servidor usa dentro de uma agenda só — aqui ela decide entre profissionais.
 */
function bestFit(days: readonly DayAvailabilityView[]): FittableSubsetView | null {
  const options = days
    .map((day) => day.fits)
    .filter((fit): fit is FittableSubsetView => fit !== null && fit !== undefined);

  return (
    options.sort(
      (a, b) => b.serviceIds.length - a.serviceIds.length || b.durationMinutes - a.durationMinutes,
    )[0] ?? null
  );
}
